import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Text,
} from '@backstage/ui';
import { BreadcrumbEntry, useRouteRef } from '@backstage/frontend-plugin-api';
import { MarkdownContent } from '@backstage/core-components';
import {
  entityRouteParams,
  entityRouteRef,
} from '@backstage/plugin-catalog-react';
import { useTaskEventStream } from '@backstage/plugin-scaffolder-react';
import {
  DefaultTemplateOutputs,
  TaskLogStream,
} from '@backstage/plugin-scaffolder-react/alpha';
import { CancelTaskDialog } from './CancelTaskDialog';
import { CreationReport } from './CreationReport';
import { DeletionSteps } from './DeletionSteps';
import { ProjectServiceCard } from './ProjectServiceCard';
import { TaskProjectBanner } from './TaskProjectBanner';
import { useTaskTemplate } from './taskTemplate';
import { useCreationFollowUp } from './creationFollowUp';
import { useProjectLifecycle } from '../shared/templateRoles';
import {
  createdRows,
  creationFacts,
  creationLayout,
  inviteOutcome,
  runningExplanation,
  taskTimes,
  type CreationFacts,
} from './creationTask';
import {
  frenchDuration,
  stepPhases,
  type StepState,
  type StepView,
} from './deletionTask';
import { waadooIcons } from '../ui/icons/waadooIcons';
import { CardTitle } from '../ui';

/** What a cancellation leaves behind, said before it is confirmed. */
const CANCEL_DESCRIPTION =
  'Les étapes suivantes ne seront pas lancées. Ce qui a déjà été créé reste en place : une fois le dépôt créé, le nom du projet n’est plus disponible pour une nouvelle création tant que ce projet n’a pas été supprimé.';

/**
 * The current time, again every `intervalMs` while `enabled`: the running
 * step's pill and the follow-up's « depuis » move without any request.
 */
function useNow(intervalMs: number, enabled: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return undefined;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs, enabled]);
  return now;
}

/** The right column while the task runs (mockup `create-running`). */
function RunningCard(props: {
  running?: StepView;
  facts?: CreationFacts;
  cancelRequested: boolean;
  /** The application's name (`taskTemplate.ts`). */
  appTitle: string;
  /** The project's type has the `standard` profile (point 8). */
  standard: boolean;
}) {
  const { running, facts, cancelRequested, appTitle, standard } = props;
  const explanation = running
    ? runningExplanation(running.id, facts, appTitle, standard)
    : undefined;
  return (
    <Card>
      <CardHeader>
        <CardTitle
          level={3}
          title={
            running ? `En cours : ${running.name}` : 'En attente du démarrage'
          }
        />
      </CardHeader>
      <CardBody>
        <div className="waadoo-creation-task__running">
          {explanation && !cancelRequested && (
            <Text as="p" variant="body-medium" color="secondary">
              {explanation}
            </Text>
          )}
          <Text as="p" variant="body-small" color="secondary">
            {cancelRequested
              ? 'Annulation demandée : aucune autre étape ne sera lancée.'
              : 'Le compte rendu et les prochaines étapes s’afficheront ici à la fin. Le journal détaillé reste accessible avec le bouton « Journal ».'}
          </Text>
        </div>
      </CardBody>
    </Card>
  );
}

/** A banner above the title, where the project banner sits while it runs. */
function ResultBanner(props: {
  status: 'success' | 'warning' | 'danger';
  title: ReactNode;
}) {
  return (
    <Container>
      <Alert
        mt="4"
        status={props.status}
        icon
        role="status"
        title={props.title}
      />
    </Container>
  );
}

/**
 * The task page of the `odoo-project` template, in place of the
 * scaffolder's (`plugin.tsx` chooses), in the look of the project deletion
 * page (`ProjectDeletionTaskPage.tsx`).
 *
 * While it runs (mockup `create-running`): the project banner, « Étapes »
 * — the timeline in four phases with the short names and grey lines of
 * `creationLayout`, the running step's pill counting —, « Annuler »,
 * « Journal » folded; right, what the running step does.
 *
 * Once done (mockup `create-B`): a green banner « Projet <p> créé en
 * <durée> », « — mise en service en cours » while followed; right, the
 * « Mise en service » card (`ProjectServiceCard`, `useCreationFollowUp`) and
 * the « Compte rendu » (`CreationReport`). On failure, a danger banner, the
 * failed step and its error, what was already created, the journal open;
 * no « Mise en service ».
 *
 * Everything comes from `useTaskEventStream`, as on the deletion page.
 */
export function ProjectCreationTaskPage(props: { taskId: string }) {
  const { taskId } = props;
  const stream = useTaskEventStream(taskId);
  const entityRoute = useRouteRef(entityRouteRef);
  const [logsOpen, setLogsOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelRequested, setCancelRequested] = useState(false);
  const logsId = useId();

  useEffect(() => {
    if (stream.error) setLogsOpen(true);
  }, [stream.error]);

  const { task } = stream;
  const template = useTaskTemplate(task);
  // Le profil du type créé (point 8) : un projet `standard` n'a ni image ni
  // premier build.
  const { profile } = useProjectLifecycle(template.appType);
  const finished = stream.completed;
  const succeeded = finished && !stream.error && !stream.cancelled;
  const facts = useMemo(
    () => (task ? creationFacts(task, stream.output) : undefined),
    [task, stream.output],
  );
  const times = useMemo(() => taskTimes(stream.steps), [stream.steps]);

  const followUp = useCreationFollowUp({
    project: facts?.project ?? '',
    ...(facts?.repoUrl ? { repoUrl: facts.repoUrl } : {}),
    ...(times.endedAt !== undefined ? { endedAt: times.endedAt } : {}),
    enabled: succeeded && facts !== undefined,
    ...(profile ? { profile } : {}),
  });
  // Every second while a step runs (its pill), every 10 s while followed.
  const now = useNow(
    finished ? 10_000 : 1000,
    Boolean(task) && (!finished || followUp.following),
  );

  const phases = useMemo(
    () =>
      task
        ? stepPhases(
            task,
            stream.steps,
            creationLayout(facts),
            finished ? undefined : now,
          )
        : [],
    [task, stream.steps, facts, finished, now],
  );

  if (!task) {
    return (
      <>
        <TaskProjectBanner taskId={taskId} />
        <Container>
          {stream.error ? (
            <Alert
              mt="4"
              status="danger"
              icon
              title="La tâche n’a pas pu être lue"
              description={stream.error.message}
            />
          ) : (
            <Text as="p" variant="body-medium" color="secondary">
              Chargement de la tâche…
            </Text>
          )}
        </Container>
      </>
    );
  }

  const steps = phases.flatMap(phase => phase.steps);
  const running = steps.find(step => step.state === 'running');
  const failed = steps.find(step => step.state === 'failed');
  const cancellable = !finished && !stream.cancelled && !cancelRequested;
  // L'état de chaque ligne, ses parties comprises (`CREATION_PARTS`).
  const states: Record<string, StepState> = Object.fromEntries(
    steps.map(step => [step.id, step.state]),
  );
  const invite = inviteOutcome(stream.stepLogs['gitlab-member']);
  const rows = facts ? createdRows(facts, states, invite) : [];

  const title =
    task.spec.templateInfo?.entity?.metadata.title ??
    `Créer un projet ${template.appTitle}`;
  const name = facts ? <strong>{facts.project}</strong> : 'Le projet';

  let banner: ReactNode;
  if (!finished) {
    banner = <TaskProjectBanner taskId={taskId} />;
  } else if (succeeded) {
    const duration =
      times.durationMs === undefined
        ? ''
        : ` en ${frenchDuration(times.durationMs)}`;
    banner = (
      <ResultBanner
        status="success"
        title={
          <>
            Projet {name} créé{duration}
            {followUp.following && ' — mise en service en cours'}
          </>
        }
      />
    );
  } else if (stream.cancelled) {
    banner = (
      <ResultBanner
        status="warning"
        title={<>Création du projet {name} annulée</>}
      />
    );
  } else {
    banner = (
      <ResultBanner
        status="danger"
        title={
          failed ? (
            <>
              Création du projet {name} interrompue — échec à l’étape «{' '}
              {failed.name} »
            </>
          ) : (
            <>La création du projet {name} a échoué</>
          )
        }
      />
    );
  }

  let main: ReactNode;
  if (stream.error) {
    main = (
      <>
        <Alert
          status="danger"
          icon
          role="alert"
          title={
            failed ? `Échec à l’étape « ${failed.name} »` : 'La tâche a échoué'
          }
          description={<MarkdownContent content={stream.error.message} />}
        />
        {facts && (
          <CreationReport
            facts={facts}
            rows={rows}
            invite={invite}
            succeeded={false}
            appTitle={template.appTitle}
          />
        )}
      </>
    );
  } else if (!finished) {
    main = (
      <RunningCard
        running={running}
        facts={facts}
        cancelRequested={cancelRequested || stream.cancelled}
        appTitle={template.appTitle}
        standard={profile === 'standard'}
      />
    );
  } else if (facts && succeeded) {
    main = (
      <>
        <ProjectServiceCard
          appType={template.appType}
          {...(profile ? { profile } : {})}
          {...(times.endedAt !== undefined ? { endedAt: times.endedAt } : {})}
          now={now}
          {...(followUp.pipeline ? { pipeline: followUp.pipeline } : {})}
          {...(followUp.app ? { app: followUp.app } : {})}
          following={followUp.following}
          staleAccess={followUp.staleAccess}
          {...(facts.pipelinesUrl ? { pipelinesUrl: facts.pipelinesUrl } : {})}
          {...(facts.devUrl ? { devUrl: facts.devUrl } : {})}
          {...(facts.repoUrl ? { repoUrl: facts.repoUrl } : {})}
          {...(facts.entityRef && entityRoute
            ? { projectHref: entityRoute(entityRouteParams(facts.entityRef)) }
            : {})}
        />
        <CreationReport
          facts={facts}
          rows={rows}
          invite={invite}
          succeeded
          appTitle={template.appTitle}
        />
      </>
    );
  } else {
    main = <DefaultTemplateOutputs output={stream.output} />;
  }

  // The banner first in a fragment, as while the task loads: the project
  // banner stays mounted from one to the other.
  return (
    <>
      {banner}
      <BreadcrumbEntry entry={{ label: taskId, href: taskId }}>
        <Container>
          <div className="waadoo-deletion-task waadoo-creation-task">
            <Text as="h2" variant="title-medium">
              {title}
            </Text>
            <div className="waadoo-deletion-task__layout">
              <Card className="waadoo-deletion-task__steps">
                <CardHeader>
                  <CardTitle level={3} title="Étapes" />
                </CardHeader>
                <CardBody>
                  <DeletionSteps phases={phases} />
                  <div className="waadoo-deletion-task__actions">
                    {cancellable && (
                      <Button
                        variant="secondary"
                        onPress={() => setCancelOpen(true)}
                      >
                        Annuler
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      aria-expanded={logsOpen}
                      aria-controls={logsId}
                      onPress={() => setLogsOpen(open => !open)}
                    >
                      Journal
                    </Button>
                  </div>
                </CardBody>
              </Card>
              <div className="waadoo-deletion-task__main waadoo-creation-task__main">
                {main}
              </div>
            </div>
            <div id={logsId} hidden={!logsOpen}>
              {logsOpen && (
                <Card>
                  <CardHeader>
                    <CardTitle
                      level={3}
                      icon={waadooIcons.logs}
                      title="Journal"
                    />
                  </CardHeader>
                  <CardBody>
                    <div className="waadoo-deletion-task__log">
                      <TaskLogStream logs={stream.stepLogs} />
                    </div>
                  </CardBody>
                </Card>
              )}
            </div>
          </div>
          <CancelTaskDialog
            taskId={taskId}
            description={CANCEL_DESCRIPTION}
            isOpen={cancelOpen}
            onOpenChange={setCancelOpen}
            onCancelled={() => setCancelRequested(true)}
          />
        </Container>
      </BreadcrumbEntry>
    </>
  );
}
